import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useApp } from '@/context/AppContext'
import AppHeader from '@/components/layout/AppHeader'
import { sendMessage as callGrok, getErrorMessage } from '@/utils/ai'
import MarkdownText from '@/components/common/MarkdownText'
import AgentRedirectBanner from '@/components/common/AgentRedirectBanner'
import { classifyIntent, PREFILL_KEY } from '@/utils/agentHandoff'
import VoiceAgent from '@/components/VoiceAgent'

// ── Suggested prompts ─────────────────────────────────────────────────────────
const SUGGESTIONS = [
  'What are my custody rights?',
  'How do I file for custody modification?',
  'What happens during a CPS investigation?',
  'What is a parenting plan?',
  'Can I record conversations with my co-parent?',
  'How is child support calculated?',
]

// ── Disclaimer message (always first) ────────────────────────────────────────
const DISCLAIMER_MSG = {
  id: 'disclaimer',
  role: 'ai',
  text:
    "👋 Hi! I'm GoodGuardian AI — your legal education assistant for family law.\n\n" +
    "I can help you understand custody rights, divorce processes, CPS interactions, and more. " +
    "I'm trained on US family law across all 50 states.\n\n" +
    "⚠️ Important: I provide legal education, not legal advice. Nothing I say is a substitute for a licensed attorney.",
  time: new Date(),
}

// ── Format relative time ───────────────────────────────────────────────────────
function formatAge(date) {
  const diffMs  = Date.now() - new Date(date).getTime()
  const diffMin = Math.floor(diffMs / 60_000)
  const diffHr  = Math.floor(diffMs / 3_600_000)
  if (diffMin < 2)  return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHr  < 2)  return `${diffHr} hour ago`
  return `${diffHr} hours ago`
}

// ── Typing indicator ──────────────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div className="flex items-end gap-2">
      <div className="w-7 h-7 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
        <span className="text-xs">🤖</span>
      </div>
      <div className="bubble-ai flex items-center gap-1 py-4 px-5">
        <span className="typing-dot w-1.5 h-1.5 rounded-full bg-slate-400 inline-block" />
        <span className="typing-dot w-1.5 h-1.5 rounded-full bg-slate-400 inline-block" />
        <span className="typing-dot w-1.5 h-1.5 rounded-full bg-slate-400 inline-block" />
      </div>
    </div>
  )
}

// ── Message bubble ─────────────────────────────────────────────────────────────
function MessageBubble({ msg }) {
  const isUser = msg.role === 'user'

  return (
    <div className={`flex items-end gap-2 ${isUser ? 'flex-row-reverse' : ''} animate-slide-up`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0 mb-1">
          <span className="text-xs">🤖</span>
        </div>
      )}
      <div className={isUser ? 'bubble-user' : 'bubble-ai'}>
        <MarkdownText content={msg.text} isUser={isUser} />
      </div>
    </div>
  )
}

// ── Suggestion chips ──────────────────────────────────────────────────────────
function SuggestionChips({ suggestions, onSelect }) {
  return (
    <div className="px-4 pb-2 flex gap-2 overflow-x-auto no-scrollbar">
      {suggestions.map(s => (
        <button
          key={s}
          onClick={() => onSelect(s)}
          className="flex-shrink-0 text-sm text-brand-700 bg-brand-50 border border-brand-200 rounded-full px-4 py-2.5 hover:bg-brand-100 transition-colors tap-target whitespace-nowrap"
        >
          {s}
        </button>
      ))}
    </div>
  )
}

// ── Error banner ──────────────────────────────────────────────────────────────
function ErrorBanner({ message, onDismiss }) {
  return (
    <div className="mx-4 mb-2 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3.5 animate-slide-up">
      <span className="text-red-500 flex-shrink-0 mt-0.5">⚠️</span>
      <p className="text-sm text-red-700 flex-1 leading-relaxed">{message}</p>
      <button onClick={onDismiss} className="text-red-400 hover:text-red-600 flex-shrink-0">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}

// ── Confirm clear dialog ───────────────────────────────────────────────────────
function ConfirmClearDialog({ onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm px-4 pb-6">
      <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-sm shadow-xl p-5 animate-slide-up border border-transparent dark:border-slate-700">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-2xl">🗑️</span>
          <h3 className="font-semibold text-slate-800 text-lg">Start new conversation?</h3>
        </div>
        <p className="text-base text-slate-500 mb-5 leading-relaxed">
          This will clear your current chat history. Your other GoodGuardian data won't be affected.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3.5 rounded-xl border border-slate-200 text-base font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-3.5 rounded-xl bg-red-500 text-white text-base font-medium hover:bg-red-600 transition-colors"
          >
            Clear & Start Fresh
          </button>
        </div>
      </div>
    </div>
  )
}

// ── New Conversation button (header right slot) ────────────────────────────────
function NewConvButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="tap-target text-white/80 hover:text-white transition-colors"
      aria-label="New conversation"
      title="New conversation"
    >
      {/* Compose / new chat icon */}
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
      </svg>
    </button>
  )
}

// ── Chat screen ───────────────────────────────────────────────────────────────
export default function Chat() {
  const { chatHistory, appendChatMessage, clearChatHistory, user } = useApp()
  const navigate  = useNavigate()
  const location  = useLocation()
  const [messages, setMessages] = useState(
    chatHistory.length > 0 ? chatHistory : [DISCLAIMER_MSG]
  )
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [error, setError] = useState(null)
  const [showConfirm, setShowConfirm] = useState(false)
  // Agent redirect state — set when classifier detects a better specialist
  const [redirect, setRedirect] = useState(null) // { agent, message }
  const bottomRef = useRef(null)
  const inputRef = useRef(null)

  const showSuggestions = messages.length <= 1

  // Pre-fill input if navigated here from another screen's handoff
  useEffect(() => {
    const prefill = location.state?.[PREFILL_KEY]
    if (prefill) {
      setInput(prefill)
      inputRef.current?.focus()
      // Clear state so refreshing doesn't re-fill
      window.history.replaceState({}, '')
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Derive the timestamp of the first real (non-disclaimer) user message
  const firstRealMsg = messages.find(m => m.id !== 'disclaimer' && m.role === 'user')
  const conversationAge = firstRealMsg ? formatAge(firstRealMsg.time) : null

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => { scrollToBottom() }, [messages, isTyping, redirect])

  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || input).trim()
    if (!trimmed || isTyping) return
    setInput('')
    setError(null)
    setRedirect(null) // clear any previous redirect suggestion

    const userMsg = { id: Date.now(), role: 'user', text: trimmed, time: new Date() }
    const updatedMessages = [...messages, userMsg]
    setMessages(updatedMessages)
    appendChatMessage(userMsg)
    setIsTyping(true)

    // Run agent classification in parallel with the chat response
    const classifyPromise = classifyIntent(trimmed, user || {})

    try {
      const reply = await callGrok(updatedMessages, user?.state)
      const aiMsg = { id: Date.now() + 1, role: 'ai', text: reply, time: new Date() }
      setMessages(prev => [...prev, aiMsg])
      appendChatMessage(aiMsg)

      // After response arrives, check if a specialist would have been a better fit
      const classification = await classifyPromise
      if (
        classification &&
        classification.agent !== 'chat' &&
        classification.confidence >= 0.75
      ) {
        setRedirect({ agent: classification.agent, message: trimmed })
      }
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setIsTyping(false)
    }
  }, [input, messages, isTyping, user, appendChatMessage])

  const handleRedirectSwitch = useCallback((route, message) => {
    setRedirect(null)
    navigate(route, { state: { [PREFILL_KEY]: message } })
  }, [navigate])

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const handleClearConfirmed = useCallback(async () => {
    setShowConfirm(false)
    await clearChatHistory()
    setMessages([DISCLAIMER_MSG])
    setError(null)
  }, [clearChatHistory])

  return (
    <div className="flex flex-col h-full bg-white lg:bg-slate-50">
      <AppHeader
        title="AI Legal Guidance"
        right={<NewConvButton onClick={() => setShowConfirm(true)} />}
      />

      {/* ── State + age badge bar ───────────────────────────────────────── */}
      {(user?.state || conversationAge) && (
        <div className="px-4 lg:px-8 py-2.5 bg-brand-50 border-b border-brand-100">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {user?.state && (
                <>
                  <span className="text-sm text-brand-600">📍 Tailored to</span>
                  <span className="text-sm font-bold text-brand-700">{user.state} family law</span>
                </>
              )}
            </div>
            {conversationAge && (
              <span className="text-sm text-slate-600">Started {conversationAge}</span>
            )}
          </div>
        </div>
      )}

      {/* ── Messages ─────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto no-scrollbar px-4 lg:px-8 py-4 lg:py-5">
        <div className="max-w-4xl mx-auto flex flex-col gap-4 lg:gap-5">
          {messages.map(msg => (
            <MessageBubble key={msg.id} msg={msg} />
          ))}
          {isTyping && <TypingIndicator />}
          {/* Agent redirect suggestion — shown after response if better agent detected */}
          {redirect && !isTyping && (
            <AgentRedirectBanner
              agent={redirect.agent}
              message={redirect.message}
              onDismiss={() => setRedirect(null)}
              onSwitch={handleRedirectSwitch}
            />
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* ── Error banner ─────────────────────────────────────────────────── */}
      {error && (
        <div className="px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <ErrorBanner message={error} onDismiss={() => setError(null)} />
          </div>
        </div>
      )}

      {/* ── Suggestions ──────────────────────────────────────────────────── */}
      {showSuggestions && (
        <div className="px-0 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <SuggestionChips
              suggestions={SUGGESTIONS}
              onSelect={text => sendMessage(text)}
            />
          </div>
        </div>
      )}

      {/* ── Input bar ────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 border-t border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 lg:px-8 py-3 lg:py-4">
        <div className="max-w-4xl mx-auto flex items-end gap-3">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about custody, rights, CPS..."
            rows={1}
            className="flex-1 input-base resize-none max-h-36 py-3 lg:py-3.5 text-sm leading-relaxed"
            style={{ minHeight: 50 }}
          />
          <VoiceAgent
            agent="chat"
            onTranscript={(text, role) => {
              if (role === 'user') setMessages(m => [...m, { role: 'user', content: text, id: Date.now() }])
              else setMessages(m => [...m, { role: 'assistant', content: text, id: Date.now() }])
            }}
          />
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || isTyping}
            className={[
              'flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-150',
              input.trim() && !isTyping
                ? 'brand-gradient text-white shadow-md active:scale-95'
                : 'bg-slate-200 text-slate-500 cursor-not-allowed',
            ].join(' ')}
            aria-label="Send message"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5 rotate-90">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5m0 0l-7 7m7-7l7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Confirm clear dialog ─────────────────────────────────────────── */}
      {showConfirm && (
        <ConfirmClearDialog
          onConfirm={handleClearConfirmed}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>
  )
}
